import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles, Paper, Theme, Typography } from "@material-ui/core";
import { AppBarSaveButton } from "@ofqual/portal-components/components/SecondaryAppBar";
import { Save } from "@material-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import { ApqModel } from "../../domain/ApqModel";
import { createItem, inputtingItem } from "../../redux/actions";
import { authSelector, createItemSelector } from "../../redux/selectors";
import { ModuleAppBar } from "../ModuleAppBar";
import { SectionProps } from "../sections/SectionProps";
import { QueryDetails } from "../sections/QueryDetails";
import { guidanceService } from "../../services";
import {
  PersistentDrawerWrapper,
  GuidanceDrawer,
} from "@ofqual/portal-components/components/Drawer";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { ExperimentalFeature } from "@ofqual/portal-components/components/ExperimentalFeature";
import { UserType } from "../../redux/types";

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      marginTop: theme.contentOffsets.AFTER_SECONDARY_APPBAR,
    },
    formSection: {
      padding: 30,
      boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
      border: "1px solid #ddd",
      backgroundColor: "#fff",
      borderRadius: "3px",
    },
  };
});
export const NewItem: React.FC = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(inputtingItem());
  }, [dispatch]);

  const classes = useStyles();
  const { userType, token } = useSelector(authSelector);

  const {
    handleSubmit,
    errors,
    control,
    formState,
    setError,
    clearErrors,
    setValue,
    getValues,
  } = useForm<ApqModel>();
  const createState = useSelector(createItemSelector);
  const { status } = createState;
  const disabled = status === "CREATING";

  const onSubmit = (model: ApqModel) => {
    dispatch(createItem(model));
  };

  const sectionProps: SectionProps<ApqModel> = {
    isCreateMode: true,
    user: { userType, token },
    disabled,
    control,
    errors,
    formState,
    clearErrors,
    setValue: (name, value) => setValue(name, value),
    getValues,
    registerCustomError: (name: keyof ApqModel, isError: boolean) => {
      const isCurrentlyARegisteredError = Object.keys(errors).includes(name);
      if (isError && !isCurrentlyARegisteredError) {
        setError(name, {});
      }
      if (!isError && isCurrentlyARegisteredError) {
        clearErrors(name);
      }
    },
  };

  const [open, setOpen] = useState(false);
  const toggleOpen = (isOpen: boolean) => setOpen(isOpen);

  const mappedGuidanceService = useMemo(
    () => ({
      get: () => {
        if (userType === UserType.Ofqual)
          return guidanceService.get(
            { module: "apqs", key: "NewOfqual-apqs" },
            { token }
          );
        else
          return guidanceService.get(
            { module: "apqs", key: "NewAo-apqs" },
            { token }
          );
      },
    }),
    [token, userType]
  );

  const { experimentalFeatures } = useAuth(authSelector);

  return (
    <>
      <PersistentDrawerWrapper open={open}>
        <div className={classes.root}>
          <ModuleAppBar breadcrumbs={[{ label: "New query" }]}>
            <AppBarSaveButton
              lifecycleLabels={["Save", "Saving", "Saved"]}
              isSubmitting={createState.status === "CREATING"}
              isSubmitted={createState.lastCrudResult.isSuccess}
              onClick={handleSubmit(onSubmit)}
              disabled={disabled}
              Icon={Save}
              dataTest="save"
            />
            <ExperimentalFeature
              experimentalModuleName="portal-apq"
              experimentalFeatures={experimentalFeatures}
              experimentalFeatureVisibleName="apqGuidancecomplete"
            >
              <GuidanceDrawer
                open={open}
                handleDrawerToggle={toggleOpen}
                service={mappedGuidanceService}
              />
            </ExperimentalFeature>
          </ModuleAppBar>
          <form>
            <Typography variant="h6">Query Details</Typography>

            <Paper className={classes.formSection}>
              <QueryDetails sectionProps={sectionProps} />
            </Paper>
          </form>
        </div>
      </PersistentDrawerWrapper>
    </>
  );
};
